import React, { useState } from "react";
import { History, Pencil, Trash2, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { cambiosDeCampo, FIELD_LABELS, formatFieldValue } from "@/lib/empleadoVersioning";

/**
 * Mini-historial de cambios de un campo versionado del empleado.
 * Props:
 *   empleado: objeto empleado (debe tener __cambios).
 *   field: nombre del campo (ej. "almuerzo_paga").
 *   onUpdated: (empleadoActualizado) => void   — callback cuando se edita/elimina una entrada.
 *   editable: boolean (default true)
 */
export default function HistorialCampoEmpleado({ empleado, field, onUpdated, editable = true }) {
  const [editing, setEditing] = useState(null); // id del cambio en edición
  const [editDate, setEditDate] = useState("");
  const [busy, setBusy] = useState(false);

  const changes = cambiosDeCampo(empleado, field);
  if (!changes.length) return null;

  const persistCambios = async (newList) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/empleados/${empleado.id}`, { __cambios: newList });
      onUpdated?.(data);
    } catch (e) {
      toast.error("No se pudo actualizar el historial: " + (e?.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async (cid) => {
    if (!editDate) return;
    const all = Array.isArray(empleado.__cambios) ? empleado.__cambios : [];
    const next = all.map(c => c && c.id === cid ? { ...c, effective_from: editDate } : c);
    await persistCambios(next);
    setEditing(null);
    toast.success("Fecha efectiva actualizada");
  };

  const removeCambio = async (cid) => {
    const ok = window.confirm(
      "¿Eliminar esta entrada del historial? El cálculo revertirá al valor anterior de esta fecha en adelante."
    );
    if (!ok) return;
    const all = Array.isArray(empleado.__cambios) ? empleado.__cambios : [];
    const next = all.filter(c => c && c.id !== cid);
    await persistCambios(next);
    toast.success("Entrada eliminada del historial");
  };

  // Construir la línea de tiempo: valor inicial (pre-primer-cambio) + cada cambio
  const first = changes[0];
  const label = FIELD_LABELS[field] || field;

  return (
    <div className="mt-2 pl-4 border-l-2 border-[#E6EFEA]" data-testid={`historial-${field}`}>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-secondary-c font-medium mb-1.5">
        <History size={10} /> Historial de {label.toLowerCase()}
      </div>
      <div className="space-y-1.5">
        <div className="text-xs text-secondary-c">
          <span className="font-medium">{formatFieldValue(field, first.old_value)}</span>
          <span className="text-secondary-c opacity-70"> (valor inicial)</span>
        </div>
        {changes.map(c => (
          <div key={c.id} className="flex items-center gap-2 text-xs flex-wrap">
            <span className="text-secondary-c">→</span>
            <span className="font-medium">{formatFieldValue(field, c.new_value)}</span>
            <span className="text-secondary-c">desde</span>
            {editing === c.id ? (
              <>
                <input
                  type="date"
                  value={editDate}
                  onChange={e => setEditDate(e.target.value)}
                  className="np-input py-0.5 px-1.5 text-[11px]"
                  autoFocus
                />
                <button
                  onClick={() => saveEdit(c.id)}
                  disabled={busy || !editDate}
                  className="np-btn np-btn-primary py-0.5 px-1.5 text-[11px]"
                  title="Guardar"
                ><Check size={10} /></button>
                <button
                  onClick={() => setEditing(null)}
                  disabled={busy}
                  className="np-btn np-btn-ghost py-0.5 px-1.5 text-[11px]"
                  title="Cancelar"
                ><X size={10} /></button>
              </>
            ) : (
              <>
                <span className="font-medium text-primary-color">{c.effective_from}</span>
                {editable && (
                  <>
                    <button
                      onClick={() => { setEditing(c.id); setEditDate(c.effective_from); }}
                      disabled={busy}
                      className="p-0.5 rounded hover:bg-[#E6EFEA] text-secondary-c hover:text-primary-color"
                      title="Editar fecha efectiva"
                      data-testid={`historial-edit-${c.id}`}
                    ><Pencil size={10} /></button>
                    <button
                      onClick={() => removeCambio(c.id)}
                      disabled={busy}
                      className="p-0.5 rounded hover:bg-[#FDF1EC] text-secondary-c hover:text-error"
                      title="Eliminar esta entrada"
                      data-testid={`historial-delete-${c.id}`}
                    ><Trash2 size={10} /></button>
                  </>
                )}
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
